<template>
  <v-app-bar app :color="navBarColor" dark clipped-left class="d-print-none">
    <v-app-bar-nav-icon @click.stop="toggleSidebar"></v-app-bar-nav-icon>
    <v-badge v-if="isDemo" color="warning" overlap content="DEMO">
      <v-img :src="logoUrl" max-width="140px" contain class="mr-2 d-none d-sm-flex" />
    </v-badge>
    <v-img v-else :src="logoUrl" max-width="140px" contain class="mr-2 d-none d-sm-flex" />
    <search-box id="search-box" v-if="!$isMobile() && $can('VIEW_NAVBAR_SEARCH')" class="mr-2" />
    <v-spacer></v-spacer>
    <!-- <ciao-booking-working-processes v-if="$can('INTERNAL_SUPPORT') && !$isMobile()" /> -->
    <v-btn icon elevation="0" :href="makeWishUrl" target="_blank">
      <v-icon>mdi-star-shooting</v-icon>
    </v-btn>
    <notifications />
    <unconfirmed-legal-compliance-requests v-if="$can('VIEW_LEGAL_COMPLIANCE')" />
    <unassigned-reservations v-if="$can('VIEW_NAVBAR_ASSIGN')" class="mr-2" />

    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-btn icon elevation="0" @click="openSettings" v-on="on" class="mr-2">
          <v-badge color="accent" overlap :content="propertyIds.length ? propertyIds.length : '0'">
            <v-icon>mdi-filter</v-icon>
          </v-badge>
        </v-btn>
      </template>
      <span>{{ $lang('main.navbar.filters.tooltip') }}</span>
    </v-tooltip>
    <v-menu offset-y>
      <template v-slot:activator="{ on }">
        <v-btn fab small v-on="on" class="mr-2" color="secondary" :title="$lang('main.navbar.support')">
            <v-icon color="primary">mdi-face-agent</v-icon>
        </v-btn>
      </template>
      <v-list dense>
        <v-subheader>{{ $lang('main.navbar.support') }}</v-subheader>
        <v-divider></v-divider>
        <v-list-item v-if="userData.is_chat_enabled" @click="toggleSupportChat">
          <v-icon class="mr-2" color="primary">mdi-face-agent</v-icon>
          {{ $lang('main.navbar.chat') }}
        </v-list-item>
        <v-list-item v-if="userData.is_ticket_enabled" :to="{ name: 'tickets'}">
          <v-icon class="mr-2" color="primary">mdi-ticket-account</v-icon>
          {{ $lang('main.titles.tickets') }}
        </v-list-item>
      </v-list>
    </v-menu> 
    <v-menu offset-y>
      <template v-slot:activator="{ on }">
        <v-btn fab small color="secondary" v-on="on" :loading="loading">
          <span class="primary--text">{{ userData.initials }}</span>
        </v-btn>
      </template>
      <v-list>
        <v-list-item @click="openUserEditing">
          <v-list-item-content>
            <v-list-item-title>{{ userData.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ userData.email }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-icon>
            <v-icon>mdi-pencil</v-icon>
          </v-list-item-icon>
        </v-list-item>
        <v-list-item @click="enableNotifications()"  v-if="!isSubscribed">
          <v-list-item-content>
            <v-list-item-title>
              <v-icon>mdi-bell</v-icon>
              {{ $lang('main.navbar.enable_notifications') }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $lang('main.navbar.enable_notifications_subtitle') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item @click="disableNotifications()" v-if="isSubscribed">
          <v-list-item-content>
            <v-list-item-title>
              <v-icon>mdi-bell-off</v-icon>
              {{ $lang('main.navbar.disable_notifications') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item :to="{name: 'user-property-clusters'}">
          <v-list-item-content>
            <v-list-item-title>
              <v-icon>mdi-home-group</v-icon>
              {{ $lang('main.titles.user_property_clusters') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-group @click.stop>
          <template v-slot:activator>
            <v-list-item-icon>
              <lang-flag class="ma-1" :iso="$locale()" :squared="false"/>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{ $lang('users.fields.locale') }}
              </v-list-item-title>
            </v-list-item-content>
          </template>
          <v-list-item @click="updateLocale(locale)" v-for="(locale, i) in dataLocale" :key="i">
             <v-list-item-icon>
              <lang-flag class="ma-1" :iso="locale" :squared="false" />
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{locale.toUpperCase()}}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>
        <v-list-item @click="clearCache">
          <v-list-item-content>
            <v-list-item-title>
              <v-icon>mdi-cached</v-icon>
              {{ $lang('main.navbar.clear_cache') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="$can('INTERNAL_SUPPORT')" @click="promptInstall()">
          <v-list-item-content>
            <v-list-item-title>
              <v-icon>mdi-checkbox-blank-badge</v-icon>
              {{ $lang('main.navbar.install_pwa') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          @click="logout"
          :loading="loading"
        >
          <v-list-item-title>
            <v-icon>mdi-logout</v-icon>
            {{ $lang('main.navbar.logout') }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <v-dialog v-model="isOpenUserEditing" width="40rem">
      <user-editing
        v-if="isOpenUserEditing"
        :item-id="userData.id"
        @success="userUpdated"
        @cancel="closeUserEditing"
      />
    </v-dialog>
  </v-app-bar>
</template>

<script>
  import { mapState } from "vuex";

  import {
    LOGOUT,
    UPDATE_AUTH_USER_LOCALE,
    SUBSCRIBE_AUTH_USER,
    UNSUBSCRIBE_AUTH_USER,
    CHECK_AUTH_USER_SUBSCRIPTION
  } from "@/store/auth/actions";

  import { DATA_LOCALE } from "@/store/data/actions";

  import {
    TOGGLE_SIDEBAR_MENU
  } from "@/store/settings/actions";

  import {
    CLEAR_CACHE,
    CLEAR_TRANSLATIONS,
  } from "@/store/app/actions";

  import MixpanelEvents from "@/constants/events";

  import data from "@/mixins/data";

  import SearchBox from "@/components/layouts/partials/SearchBox";
  import Notifications from "@/components/layouts/partials/Notifications";
  import UnassignedReservations from "@/components/layouts/partials/UnassignedReservations";
  import UnconfirmedLegalComplianceRequests from "@/components/layouts/partials/UnconfirmedLegalComplianceRequests";
  // import CiaoBookingWorkingProcesses from "@/components/layouts/partials/WorkingProcesses";
  import UserEditing from "@/pages/users/SimpleEditingForm";

  export default {
    name: "CiaoBookingNavbar",
    components: {
      SearchBox,
      Notifications,
      UnassignedReservations,
      UnconfirmedLegalComplianceRequests,
      UserEditing,
    },
    mixins: [
      data
    ],
    data() {
      return {
        isOpenUserEditing: false,
        isPwaInstalled: false,
      };
    },
    methods: {
      toggleSidebar() {
        this.$store.dispatch(TOGGLE_SIDEBAR_MENU, this.$isMobile());

        this.$monitorService.track(MixpanelEvents.MENU_COLLAPSED, {
          minimized: this.sidebar.minimized
        });
      },
      openSettings() {
        this.$emit("open-settings");
      },
      logout() {
        zE("messenger", "logoutUser");
        this.$monitorService.reset();
        this.$store.dispatch(LOGOUT).then(() => {
          this.$router.push({ name: "login" });
        });
      },
      updateLocale(locale) {
        if (this.$locale() === locale) {
          return false;
        }

        this.$store.dispatch(UPDATE_AUTH_USER_LOCALE, {
          locale
        }).then(() => {
          this.clearTranslationsCache();
        });
      },
      toggleSupportChat() {
        // if (this.$Tawk.isChatMaximized() || !this.$Tawk.isChatHidden()) {
        //   this.$Tawk.minimize();
        //   this.$Tawk.hideWidget();
        // } else {
        //   this.$Tawk.showWidget();
        //   this.$Tawk.maximize();
        // }
        zE("messenger", "hide");
      },
      enableNotifications() {
        this.$store.dispatch(SUBSCRIBE_AUTH_USER);
        this.$monitorService.track(MixpanelEvents.WEBPUSH_NOTIFICATIONS_ENABLED);
      },
      disableNotifications() {
        this.$store.dispatch(UNSUBSCRIBE_AUTH_USER);
        this.$monitorService.track(MixpanelEvents.WEBPUSH_NOTIFICATIONS_DISABLED);
      },
      checkSubscriptionStatus() {
        this.$store.dispatch(CHECK_AUTH_USER_SUBSCRIPTION);
      },
      clearCache() {
        this.$store.dispatch(CLEAR_CACHE).then(() => {
          location.reload();
        });
      },
      clearTranslationsCache() {
        this.$store.dispatch(CLEAR_TRANSLATIONS).then(() => {
          //location.reload();
        });
      },
      openUserEditing() {
        this.isOpenUserEditing = true;
      },
      userUpdated() {
        this.isOpenUserEditing = false;
      },
      closeUserEditing() {
        this.isOpenUserEditing = false;
      },
      async checkIfPWAIsInstalled() {
        if (
          typeof window == undefined ||
          !navigator.getInstalledRelatedApps
        ) return;

        const relatedApps = await navigator.getInstalledRelatedApps();
        this.isPwaInstalled = !!relatedApps.length || !!this.deferredPrompt;
      }
    },
    created() {
      this.$store.dispatch(DATA_LOCALE);
      this.checkSubscriptionStatus();
    },
    mounted() {
      this.checkIfPWAIsInstalled();
    },
    computed: {
      ...mapState({
        loading: state => state.auth.logoutRequest.loading,
        localeLoading: state => state.auth.updateUserLocaleRequest.loading,
        propertyIds: state => state.settings.property.ids,
        isSubscribed: state => state.auth.isSubscribed,
        whitelabel: state => state.settings.whitelabel,
        sidebar: state => state.settings.sidebar,
        isDemo: state => state.app.isDemo,
      }),
      navBarColor() {
        if (this.whitelabel.color) {
          return this.whitelabel.color;
        }

        if (this.userData.email) {
          if (this.userData.email.includes('a.dugo')) {
            return 'pink accent-1'
          }

          if (this.userData.email.includes('j.ergazzori')) {
            return 'black';
          }
        }

        if (this.$can('INTERNAL_SUPPORT') || this.$role('INTERNAL_SUPPORT')) {
          return 'red darken-4';
        }

        if (this.$can('INTERNAL_SALES')) {
          return 'cyan darken-2';
        }

        if (this.$role('USER_ADMIN')) {
          return 'teal darken-3'
        }

        if (this.$role('MANAGER')) {
          return 'blue-grey darken-3'
        }

        if (this.$role('STAFF')) {
          return 'orange darken-4'
        }

        return 'primary'
      },
      logoUrl() {
        if (this.whitelabel.image) {
          return this.whitelabel.image;
        }

        return '/img/logo-white.svg';
      },
      makeWishUrl() {
        if (this.$locale() === 'it') {
          return 'https://forms.gle/LYVmphu5SC4KgBFf7';
        }
        if (this.$locale() === 'de') {
          return 'https://forms.gle/mwZigW7Q2aZYzSXg9';
        }
        return 'https://forms.gle/PZeuZRjSFywCvGt58';
      }
    }
  };
</script>

<style scoped>
#search-box {
  margin-left: 55px;
}
</style>
